import {ShapeType} from "@luciad/ria/shape/ShapeType.js";
import {Handle} from "@luciad/ria/util/Evented.js";
import {BasicCreateController} from "@luciad/ria/view/controller/BasicCreateController.js";
import {EditController} from "@luciad/ria/view/controller/EditController.js";
import {Layer} from "@luciad/ria/view/Layer.js";
import {LayerTreeNode} from "@luciad/ria/view/LayerTreeNode.js";
import {LayerTreeVisitor} from "@luciad/ria/view/LayerTreeVisitor.js";
import {Map} from "@luciad/ria/view/Map.js";
import * as React from "react";
import {Bounds, SpatialModel} from "../../../model";
import {Logger} from "../../util/Logger";
import {Spinner} from "../spinner/Spinner";
import {LayerTree} from "./LayerTree";
import {BackgroundMetadata} from "./model";
import {createDrawLayer, DRAW_ID} from "./RiaApi";
import * as riaApi from "./RiaApi";
import {FormattedMessage} from "react-intl";
import {Button} from "react-bootstrap";
import {FeatureLayer} from "@luciad/ria/view/feature/FeatureLayer.js";
import {setStateAsync} from "../../util/ReactUtils";
import {FeatureModel} from "@luciad/ria/model/feature/FeatureModel.js";
import {LineType} from "@luciad/ria/geodesy/LineType.js";
import {Feature} from "@luciad/ria/model/feature/Feature.js";
import {ApolloApi} from "../../../api/ApolloApi";
import {oidcProviderInfo} from "../../../oidc/startOidcProvider";

interface RIAMapProps {
    wmsStyle?:string;
    style: React.CSSProperties;
    getLayernameFunction?: (data: SpatialModel) => string;
    getWmsUrlFunction?: (data: SpatialModel) => string;
    background?: BackgroundMetadata;
    wmsUrl?: string;
    dataToVisualize: SpatialModel[];
    shouldFit: boolean;
    shouldAnimateFit: boolean;
    shouldAddGridLayer: boolean;
    shouldHideLayerTree?: boolean;
    wmsReference?: string;
    mapState?: object;
    onMapIdle?: (mapState) => void;
    restrictNavigationBounds?: Bounds;
    onMapInitialized?: (map: Map) => void;
    showPreview: boolean;
    backgroundDataPreview?: boolean;
    backgroundSequenceNumber?: number;
    drawModeActive?: boolean;
    editModeActive?: boolean;
    onDrawCancel?: () => void;
    onEditCancel?: () => void;
    editFeatureCoordinates?: number[];
    alwaysShowEditFeature?: boolean;
    onFeatureDrawn?: (feature: Feature) => boolean;
    button?: JSX.Element;
}

interface RIAMapState {
    busyLoading: boolean;
    cancelDrawing: boolean;
    token: string;
}

const waitForSize = (domNode: Element) => {
    return new Promise<void>((resolve) => {
        const checkSize = () => {
            const rect = domNode.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
                resolve();
            } else {
                setTimeout(checkSize, 10);
            }
        };
        checkSize();
    });
};

export class RIAMap extends React.Component<RIAMapProps, RIAMapState> {
    _logger: Logger = Logger.getLogger("RIAMap");
    mapRef: React.ReactInstance;
    map: Map;
    initMapPromise: Promise<void>;
    mapIdleListener: Handle;
    backgroundLayers: Layer[];
    drawLayer: FeatureLayer;
    drawLayerWorkingSetChangedHandle: Handle;

    constructor(props) {
        super(props);
        this.state = {
            busyLoading: false,
            cancelDrawing: false,
            token: null,
        };
    }

    createMap = (nextProps) => {
        //awkward... The component is mounted before the DOM node is styled and has a height
        this.initMapPromise = waitForSize(this.mapRef as Element).then(() => {
            this.initMap(nextProps);
        }).catch((error) => {
            this._logger.error("Error occurred while initializing map");
        });
    }

    destroyMap = () => {
        if (this.mapIdleListener) {
            this.mapIdleListener.remove();
        }
        riaApi.destroyMap(this.map);
    }

    clearMap = () => {
        const nonBackgroundLayers = [];
        this.map.layerTree.visitChildren({
            visitLayer: (layer) => {
                if (this.backgroundLayers.indexOf(layer) < 0) {
                    nonBackgroundLayers.push(layer);
                }
                return LayerTreeVisitor.ReturnValue.CONTINUE;
            },
            visitLayerGroup: () => {
                return LayerTreeVisitor.ReturnValue.CONTINUE;
            },
        }, LayerTreeNode.VisitOrder.TOP_DOWN);
        nonBackgroundLayers.forEach((nonBackgroundLayer) => this.map.layerTree.removeChild(nonBackgroundLayer));
    }

    initLayers = (props: RIAMapProps) => {
        const {wmsUrl, shouldFit, shouldAnimateFit, dataToVisualize, showPreview, alwaysShowEditFeature} = props;

        let aoiBounds = null;
        if (!showPreview) {
            aoiBounds = riaApi.getBounds(dataToVisualize);
        }
        riaApi.setAreaOfInterest(this.map, aoiBounds);

        const canLoad = !!(wmsUrl && dataToVisualize && dataToVisualize.length && showPreview);
        this.setLoadingState(canLoad);

        if (canLoad) {
            const layerReference = this.map.reference;
            const defaultBounds = this.map.mapBounds;

            const layersLoadedPromises = [];

            // RIA layers should be created in reversed order - last item is painted first
            const reversedData = [...dataToVisualize].reverse();

            reversedData.forEach((dataItem) => {
                const controlRoomBounds = dataItem.wgs84Bounds;
                const riaBounds = controlRoomBounds ? riaApi.createBounds(controlRoomBounds) : defaultBounds;
                const layerName = wmsUrl.substr(wmsUrl.lastIndexOf("/") + 1) + dataItem.id;
                const layerTitle = dataItem.title || dataItem.name;
                const url = wmsUrl + dataItem.id;

                const layer = this.props.wmsStyle ?
                    riaApi.addWMSLayerToMapWithStyle(this.map, url, layerName, layerTitle, riaBounds, layerReference, this.props.wmsStyle) :
                    riaApi.addWMSLayerToMap(this.map, url, layerName, layerTitle, riaBounds, layerReference)
                layersLoadedPromises.push(layer.whenReady());
            });

            Promise.all(layersLoadedPromises).then(() => {
                setTimeout(() => this.setLoadingState(false), 500);
            }).catch((error) => {
                this._logger.error("Error occured while loading layers", error);
            });
        }

        this.createDrawLayer();
        if (shouldFit) {
            const reactorBounds = dataToVisualize.map((dataItem) => {
                return dataItem.wgs84Bounds;
            });
            if (alwaysShowEditFeature) {
                const editFeature = this.addDrawFeature();
                editFeature && reactorBounds.push(editFeature.shape.bounds);
            }
            if (reactorBounds.length > 0) {
                riaApi.fitOnBounds(this.map, reactorBounds, shouldAnimateFit);
            } else {
                const worldBounds = {x: -180, y: -88, width: 360, height: 88 * 2};
                riaApi.fitOnBounds(this.map, [worldBounds], shouldAnimateFit);
            }
        }
    }

    private createDrawLayer() {
        const layer = createDrawLayer();
        this.map.layerTree.addChild(layer);
        this.drawLayer = layer;
    }

    private onDrawLayerWorkingSetChanged = (eventType, feature, _id) => {
        const {onFeatureDrawn} = this.props;
        if (this.state.cancelDrawing) {
            this.clearDrawLayer(feature.id);
            return;
        }
        if ((eventType === "add" || eventType === "update") && onFeatureDrawn) {
            if (onFeatureDrawn(feature)) {
                this.clearDrawLayer(feature.id);
            }
        }
    };

    private clearDrawLayer(featureId) {
        this.drawLayerWorkingSetChangedHandle && this.drawLayerWorkingSetChangedHandle.remove();
        (this.drawLayer.model as FeatureModel).remove(featureId);
    }

    setLoadingState = (state: boolean) => {
        this.setState(Object.assign({}, this.state, {busyLoading: state}));
    }

    initMap = (nextProps) => {
        const props = nextProps ? nextProps : this.props;
        const {wmsReference, shouldAddGridLayer, restrictNavigationBounds, background} = props;

        if (props.backgroundDataPreview) {
            this.setLoadingState(true);
        }
        this.map = riaApi.createMap(this.mapRef, shouldAddGridLayer, restrictNavigationBounds, wmsReference, background);
        this.backgroundLayers = [];
        const backgroundLayersLoadedPromises = [];
        //track background layers, these don't need to be removed upon switching layers
        this.map.layerTree.visitChildren({
            visitLayer: (layer) => {
                this.backgroundLayers.push(layer);
                if (props.backgroundDataPreview) {
                    backgroundLayersLoadedPromises.push(layer.whenReady());
                }
                return LayerTreeVisitor.ReturnValue.CONTINUE;
            },
            visitLayerGroup: () => {
                return LayerTreeVisitor.ReturnValue.CONTINUE;
            },
        }, LayerTreeNode.VisitOrder.TOP_DOWN);

        if (props.backgroundDataPreview) {
            Promise.all(backgroundLayersLoadedPromises).then(() => {
                setTimeout(() => this.setLoadingState(false), 500);
            }).catch((error) => {
                this._logger.error("Error occurred while loading background layers", error);
            });
        } else {
            this.initLayers(props);
        }

        if (this.props.mapState) {
            this.map.restoreState(this.props.mapState).catch((error) => {
                this._logger.error("Error occurred while restoring map state", error);
            });
        }
        if (this.props.onMapIdle) {
            this.mapIdleListener = this.map.on("idle", () => {
                const state = this.map.saveState();
                this.props.onMapIdle(state);
            });
        }
        if (this.props.onMapInitialized) {
            this.props.onMapInitialized(this.map);
        }
    }

    checkToken = () => {
        const token = ApolloApi.getOidcTokenValue();
        if (this.map && this.state.token !== token) {
            const bounds = this.map.mapBounds;
            riaApi.destroyMap(this.map);
            this.initMap(this.props);
            this.map.mapNavigator.fit({bounds, animate: false}).then(r => {
                this.updateState({token});
            });
        }
    }

    componentDidMount() {
        if (oidcProviderInfo?.enabled) {
            this.updateState({token: ApolloApi.getOidcTokenValue()});
            setInterval(this.checkToken, 1000);
        }
        this.createMap(null);
    }

    componentDidUpdate(prevProps: RIAMapProps) {
        if (!this.map) {
            return;
        }

        this.initMapPromise
            .then(() => {
                const {drawModeActive, editModeActive, alwaysShowEditFeature} = this.props;

                if (drawModeActive && (!prevProps.drawModeActive || !(this.map.controller instanceof BasicCreateController))) {
                    this.map.controller = new BasicCreateController(ShapeType.POLYGON, {});
                    (this.drawLayer.model as FeatureModel).remove(DRAW_ID);
                    this.drawLayerWorkingSetChangedHandle = this.drawLayer.workingSet.on("WorkingSetChanged", this.onDrawLayerWorkingSetChanged);
                    this.state.cancelDrawing && this.updateState({cancelDrawing: false});
                } else if (editModeActive && (!prevProps.editModeActive || !(this.map.controller instanceof EditController))) {
                    const editFeature = this.addDrawFeature();

                    this.map.controller = this.createEditController(editFeature);

                    this.drawLayerWorkingSetChangedHandle = this.drawLayer.workingSet.on("WorkingSetChanged", this.onDrawLayerWorkingSetChanged);
                    this.state.cancelDrawing && this.updateState({cancelDrawing: false});
                } else if (!drawModeActive && !editModeActive) {
                    this.map.controller = null;

                    if (alwaysShowEditFeature && !(this.drawLayer.model as FeatureModel).store.get(DRAW_ID)) {
                        this.addDrawFeature();
                    }
                }
            });
    }

    private addDrawFeature() {
        const {editFeatureCoordinates} = this.props;

        let editFeature: Feature = null;
        if (editFeatureCoordinates?.length > 5) {
            editFeature = riaApi.createFeature(editFeatureCoordinates);

            (this.drawLayer.model as FeatureModel).remove(DRAW_ID);
            (this.drawLayer.model as FeatureModel).add(editFeature);
        }

        return editFeature;
    }

    private createEditController(editFeature: Feature) {
        const editController = new EditController(this.drawLayer, editFeature);


        // TODO: temporary solution, workaround to place edit handles on polygon edges
        // submitted to Luciad RIA support, Ticket#2021032910000141
        let editingContext = null;
        Object.defineProperty(editController, "_editingContext", {
            get: () => {
                return editingContext;
            },
            set: (v) => {
                editingContext = v;

                const geodesy = editingContext._handle._handles[0]._handles[0]._handles[2]._geodesy;
                const geodesyInterpolate = geodesy.interpolate.bind(geodesy);
                geodesy.interpolate = (startPoint, endPoint, fraction) => {
                    return geodesyInterpolate(startPoint, endPoint, fraction, LineType.CONSTANT_BEARING);
                };

                editingContext._handle.update();
            }
        });

        return editController;
    }

    componentWillUnmount() {
        this.destroyMap();
    }

    wmsUlrHasChanged(nextProps: RIAMapProps) {
        return (this.props.wmsUrl && (this.props.wmsUrl !== nextProps.wmsUrl));
    }

    dataToVisualizeHasChanged(nextProps: RIAMapProps) {
        const thisData = this.props.dataToVisualize;
        const nextData = nextProps.dataToVisualize;
        if (thisData && nextData) {
            if (thisData.length !== nextData.length) {
                return true;
            }
            let someItemChanged = false;
            for (let i = 0; i < thisData.length; i++) {
                const idChanged = thisData[i].id !== nextData[i].id;
                const sequenceNumberChanged = thisData[i].sequenceNumber !== nextData[i].sequenceNumber;
                // Check if content has changed for styles
                const contentChanged = (thisData[i] as any).content && (nextData[i] as any).content && (thisData[i] as any).content !== (nextData[i] as any).content;
                someItemChanged = someItemChanged || (idChanged || sequenceNumberChanged || contentChanged);
                if (someItemChanged) {
                    break;
                }
            }
            return someItemChanged;
        }
        //one of the two is null or undefined
        return nextData === thisData;
    }

    shouldPreviewHasChanged(nextProps) {
        return (this.props.showPreview !== nextProps.showPreview);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.map && (this.wmsUlrHasChanged(nextProps) || this.dataToVisualizeHasChanged(nextProps) ||
            this.shouldPreviewHasChanged(nextProps))) {
            this.clearMap();
            this.initLayers(nextProps);
        } else if (this.map && this.props.backgroundSequenceNumber && nextProps.backgroundSequenceNumber
            && this.props.backgroundSequenceNumber !== nextProps.backgroundSequenceNumber) {
            this.destroyMap();
            this.initMap(nextProps);
        }
    }

    updateState = (updatedPart) => this.setState(Object.assign({}, this.state, updatedPart));

    render() {
        const layerTree = this.props.shouldHideLayerTree ? null : <LayerTree map={this.map}/>;
        const loadingUI = this.state.busyLoading ? (<div className="spinnerContainer"><Spinner bigStyle={true}/></div>)
            : null;

        return (
            <div className="riamap" data-cy="map" ref={(ref) => {
                this.mapRef = ref;
            }} style={this.props.style}>
                {this.renderButtons()}
                {this.renderMessage()}
                {loadingUI}
                {layerTree}
            </div>
        );
    }

    private renderMessage() {
        if (!this.props.drawModeActive && !this.props.editModeActive) {
            return;
        }
        return <div className="riamapMessage">
            <FormattedMessage
                id="studio.ria-map.double-click-to-finish"
                defaultMessage="Double click to finish"/>
        </div>;
    }

    private renderButtons() {
        if (!this.props.drawModeActive && !this.props.editModeActive) {
            return this.props.button;
        }
        return <div className="riamapButtons">
            <Button data-cy="cancel-drawing-btn" onClick={this.handleCancelDrawing}>
                <FormattedMessage
                    id="studio.ria-map.cancel-drawing"
                    defaultMessage="Cancel"/>
            </Button>
        </div>
    }

    private handleCancelDrawing = () => {
        setStateAsync(this, {cancelDrawing: true})
            .then(() => {
                if (this.props.drawModeActive) {
                    this.props.onDrawCancel();
                }
                if (this.props.editModeActive) {
                    this.props.onEditCancel();
                }
            });
    }
}